import { ChangeEvent } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ExpressAccount } from "../../types/express-master.type";
import { formatNumber } from "../../utils";
import MasterPicker from "../ExpressMaster/MasterPicker";
import { searchActiveAccounts } from "../ExpressMaster/masterSearch";
import CellTextField from "./CellTextField";
import {
  ExpressDocTypeConfig,
  MONEY_DECIMALS,
  QTY_DECIMALS,
  UNIT_PRICE_DECIMALS,
  WAREHOUSE_CODE_MAX_LENGTH,
} from "./constants";
import { LineDraft, draftLineAmount, newLine, sanitizeDecimal } from "./documentDraft";
import { gridHeadCellSx, gridHeadSx } from "./gridStyles";

interface LinesGridProps {
  config: ExpressDocTypeConfig;
  lines: LineDraft[];
  readOnly: boolean;
  /** The department a blank คลัง falls back to; unknown until the document is saved. */
  departmentCode: string;
  error?: string;
  onChange: (lines: LineDraft[]) => void;
}

type TextChange = (event: ChangeEvent<HTMLInputElement>) => void;

const ACCOUNT_PICKER_MIN_WIDTH = 220;
const FIGURE_WIDTH = 120;


/** A blank คลัง is booked to the department, which a new document learns when it is saved. */
const WAREHOUSE_PLACEHOLDER = "ตามแผนก";

interface LineRowProps {
  config: ExpressDocTypeConfig;
  line: LineDraft;
  lineNo: number;
  readOnly: boolean;
  departmentCode: string;
  onUpdate: (line: LineDraft) => void;
  onRemove: (key: string) => void;
}

function LineRow({ config, line, lineNo, readOnly, departmentCode, onUpdate, onRemove }: Readonly<LineRowProps>) {
  const isQtyPrice = config.lineMode === "QTY_PRICE";
  const amountText = formatNumber(draftLineAmount(line, config));

  /** The description starts as the account's name and stays editable. */
  const handleAccountChange = (account: ExpressAccount | null) =>
    onUpdate({
      ...line,
      account: account ? { code: account.code, name: account.name } : null,
      description: account ? account.name : line.description,
    });
  const handleDescriptionChange: TextChange = (event) =>
    onUpdate({ ...line, description: event.target.value });
  const handleWarehouseChange: TextChange = (event) =>
    onUpdate({ ...line, warehouseCode: event.target.value.trim() });
  const handleQtyChange: TextChange = (event) =>
    onUpdate({ ...line, qty: sanitizeDecimal(event.target.value, QTY_DECIMALS) });
  // A negative unit price offsets the other lines, e.g. the petty cash a P4 is paid from.
  const handleUnitPriceChange: TextChange = (event) =>
    onUpdate({ ...line, unitPrice: sanitizeDecimal(event.target.value, UNIT_PRICE_DECIMALS, true) });
  const handleAmountChange: TextChange = (event) =>
    onUpdate({ ...line, amount: sanitizeDecimal(event.target.value, MONEY_DECIMALS) });
  const handleRemove = () => onRemove(line.key);

  return (
    <TableRow>
      <TableCell>{lineNo}</TableCell>
      <TableCell>
        {readOnly ? (
          <Typography variant="body2">{line.account?.code}</Typography>
        ) : (
          <MasterPicker
            value={line.account}
            search={searchActiveAccounts}
            onChange={handleAccountChange}
            placeholder="ค้นหารหัสบัญชี"
            minWidth={ACCOUNT_PICKER_MIN_WIDTH}
          />
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        <CellTextField
          value={line.description}
          readOnly={readOnly}
          ariaLabel={`รายละเอียดบรรทัด ${lineNo}`}
          onChange={handleDescriptionChange}
        />
      </TableCell>
      {isQtyPrice && (
        <>
          <TableCell>
            <CellTextField
              value={line.warehouseCode}
              readOnly={readOnly}
              placeholder={departmentCode || WAREHOUSE_PLACEHOLDER}
              ariaLabel={`คลังบรรทัด ${lineNo}`}
              width={80}
              maxLength={WAREHOUSE_CODE_MAX_LENGTH}
              onChange={handleWarehouseChange}
            />
          </TableCell>
          <TableCell align="right">
            <CellTextField
              value={line.qty}
              readOnly={readOnly}
              displayValue={formatNumber(line.qty)}
              ariaLabel={`จำนวนบรรทัด ${lineNo}`}
              isNumeric
              width={FIGURE_WIDTH}
              onChange={handleQtyChange}
            />
          </TableCell>
          <TableCell align="right">
            <CellTextField
              value={line.unitPrice}
              readOnly={readOnly}
              displayValue={formatNumber(line.unitPrice)}
              ariaLabel={`ราคาต่อหน่วยบรรทัด ${lineNo}`}
              isNumeric
              width={FIGURE_WIDTH}
              onChange={handleUnitPriceChange}
            />
          </TableCell>
          <TableCell align="right">{amountText}</TableCell>
        </>
      )}
      {!isQtyPrice && (
        <TableCell align="right">
          <CellTextField
            value={line.amount}
            readOnly={readOnly}
            displayValue={amountText}
            ariaLabel={`จำนวนเงินบรรทัด ${lineNo}`}
            isNumeric
            width={FIGURE_WIDTH}
            onChange={handleAmountChange}
          />
        </TableCell>
      )}
      {!readOnly && (
        <TableCell align="center">
          <Tooltip title="ลบบรรทัด">
            <IconButton size="small" onClick={handleRemove} aria-label={`ลบบรรทัด ${lineNo}`}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  );
}

/** รายการสินค้า: the account lines of R5 / P4 (qty × unit price) and O1 (an amount each). */
export default function LinesGrid({
  config,
  lines,
  readOnly,
  departmentCode,
  error,
  onChange,
}: Readonly<LinesGridProps>) {
  const isQtyPrice = config.lineMode === "QTY_PRICE";

  const handleUpdate = (updated: LineDraft) =>
    onChange(lines.map((line) => (line.key === updated.key ? updated : line)));
  const handleRemove = (key: string) => onChange(lines.filter((line) => line.key !== key));
  const handleAdd = () => onChange([...lines, newLine()]);

  return (
    <Stack gap={1}>
      {error && <Alert severity="error">{error}</Alert>}
      <TableContainer>
        <Table size="small">
          <TableHead sx={gridHeadSx}>
            <TableRow>
              <TableCell sx={gridHeadCellSx} width={48}>
                No.
              </TableCell>
              <TableCell sx={gridHeadCellSx}>รหัส</TableCell>
              <TableCell sx={gridHeadCellSx}>รายละเอียด</TableCell>
              {isQtyPrice && (
                <>
                  <TableCell sx={gridHeadCellSx}>คลัง</TableCell>
                  <TableCell sx={gridHeadCellSx} align="right">
                    จำนวน
                  </TableCell>
                  <TableCell sx={gridHeadCellSx} align="right">
                    ราคาต่อหน่วย
                  </TableCell>
                </>
              )}
              <TableCell sx={gridHeadCellSx} align="right">
                จำนวนเงิน
              </TableCell>
              {!readOnly && <TableCell width={56} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <LineRow
                key={line.key}
                config={config}
                line={line}
                lineNo={index + 1}
                readOnly={readOnly}
                departmentCode={departmentCode}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {lines.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
          ยังไม่มีรายการ
        </Typography>
      )}
      {!readOnly && (
        <Stack direction="row">
          <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
            เพิ่มบรรทัด
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
